import { Search } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useHasPermission } from "@/hooks/use-auth";
import { useIsMobile } from "@/hooks/use-mobile";

import { ProductionDesktopTable } from "./production-desktop-table";
import { ProductionMobileList } from "./production-mobile-list";
import { filterProductions } from "./production-utils";

import type { StockTransformation } from "@artisancode/api-types";

interface Props {
  transformations: StockTransformation[];
  searchQuery: string;
  onSearchQueryChange: (q: string) => void;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  onViewDetail: (trans: StockTransformation) => void;
  onComplete: (id: string) => void;
  onCancelRequest: (trans: StockTransformation) => void;
  isCompleting?: boolean;
}

export function ProductionTableCard({
  transformations,
  searchQuery,
  onSearchQueryChange,
  statusFilter,
  onStatusFilterChange,
  onViewDetail,
  onComplete,
  onCancelRequest,
  isCompleting,
}: Props) {
  const isMobile = useIsMobile();
  const canComplete = useHasPermission("stock_transformations.complete");
  const filtered = filterProductions(
    transformations,
    searchQuery,
    statusFilter,
  );
  const rowsProps = {
    rows: filtered,
    canComplete,
    isCompleting,
    onViewDetail,
    onComplete,
    onCancelRequest,
  };

  return (
    <Card className="shadow-xs">
      <CardHeader className="p-4 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-base font-semibold">
              Daftar Batch Produksi
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              Menampilkan {filtered.length} dari {transformations.length} total
              dokumen batch
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cari kode, produk, atau catatan..."
                value={searchQuery}
                onChange={(e) => onSearchQueryChange(e.target.value)}
                className="pl-8 text-sm h-9"
              />
            </div>

            <Select value={statusFilter} onValueChange={onStatusFilterChange}>
              <SelectTrigger className="w-full sm:w-[140px] h-9 text-xs">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="completed">Selesai</SelectItem>
                <SelectItem value="cancelled">Dibatalkan</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {isMobile ? (
          <ProductionMobileList {...rowsProps} />
        ) : (
          <ProductionDesktopTable {...rowsProps} />
        )}
      </CardContent>
    </Card>
  );
}
